import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  format,
  parseISO,
  isValid,
  addMonths,
  addYears,
  startOfMonth,
  startOfWeek,
  addDays,
  isBefore,
  startOfDay
} from 'date-fns';
import { cn } from '../../lib/utils';
import { Label } from '../ui/label';
import { CalendarIcon } from '../Icons/svgIcons';

const VIEW_MODES = {
  DAY: 'day',
  MONTH: 'month',
  YEAR: 'year'
};

const PRIMARY = '#166c98';

const parseValue = v => {
  if (!v) return undefined;
  if (v instanceof Date) return isValid(v) ? v : undefined;
  if (typeof v === 'string') {
    try {
      const parsed = parseISO(v);
      return isValid(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const SingleDatePicker = React.forwardRef(
  (
    {
      label,
      value,
      onChange,
      placeholder = 'Select date',
      isRequired,
      error,
      disabled,
      minDate,
      className
    },ref
  ) => {
    const initialParsed = parseValue(value);
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState(VIEW_MODES.DAY);
    const [visibleDate, setVisibleDate] = useState(initialParsed || new Date());
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);

    const selectedDate = parseValue(value);

    const updatePosition = () => {
      if (!triggerRef.current || !popoverRef.current) return;
      
      const trigger = triggerRef.current.getBoundingClientRect();
      const popover = popoverRef.current;
      const viewportHeight = window.innerHeight;

      const triggerBottomPercent = (trigger.bottom / viewportHeight) * 100;
      const shouldOpenUpward = triggerBottomPercent > 70;
      
      popover.style.position = 'fixed';
      popover.style.left = `${trigger.left}px`;
      
      if (shouldOpenUpward) {
        popover.style.bottom = `${viewportHeight - trigger.top + 4}px`;
        popover.style.top = 'auto';
      } else {
        popover.style.top = `${trigger.bottom + 4}px`;
        popover.style.bottom = 'auto';
      }
    };

    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = e => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target)
        ) {
          setIsOpen(false);
        }
      };

      const handleEscape = e => {
        if (e.key === 'Escape') setIsOpen(false);
      };

      const handleScroll = () => {
        updatePosition();
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      requestAnimationFrame(updatePosition);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }, [isOpen]);

    const isSameDay = (a, b) => {
      if (!a || !b) return false;
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    };

    const handleSelect = date => {
      if (date) onChange && onChange(format(date, 'yyyy-MM-dd'));
      else onChange && onChange(null);
      setIsOpen(false);
    };

    const handleDayClick = date => {
      handleSelect(date);
    };

    const handleMonthCellClick = monthIndex => {
      const year = visibleDate.getFullYear();
      const next = new Date(year, monthIndex, 1);
      setVisibleDate(next);
      setViewMode(VIEW_MODES.DAY);
    };

    const handleYearCellClick = year => {
      const next = new Date(year, visibleDate.getMonth(), 1);
      setVisibleDate(next);
      setViewMode(VIEW_MODES.MONTH);
    };

    const handlePrev = () => {
      if (viewMode === VIEW_MODES.DAY) {
        setVisibleDate(addMonths(visibleDate, -1));
      } else if (viewMode === VIEW_MODES.MONTH) {
        setVisibleDate(addYears(visibleDate, -1));
      } else {
        setVisibleDate(addYears(visibleDate, -12));
      }
    };

    const handleNext = () => {
      if (viewMode === VIEW_MODES.DAY) {
        setVisibleDate(addMonths(visibleDate, 1));
      } else if (viewMode === VIEW_MODES.MONTH) {
        setVisibleDate(addYears(visibleDate, 1));
      } else {
        setVisibleDate(addYears(visibleDate, 12));
      }
    };

    const setView = mode => {
      setViewMode(mode);
    };

    const renderDayGrid = () => {
      const monthStart = startOfMonth(visibleDate);
      const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const days = [];
      let current = gridStart;
      while (days.length < 42) {
        days.push(current);
        current = addDays(current, 1);
      }
      const weeks = [];
      for (let i = 0; i < 6; i += 1) {
        weeks.push(days.slice(i * 7, i * 7 + 7));
      }

      return (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-7 text-[11px] font-medium text-slate-500 px-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} className="text-center py-1 uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-rows-6 gap-1 px-1 pb-1">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map(date => {
                  const inMonth = date.getMonth() === visibleDate.getMonth();
                  const isSelected = selectedDate && isSameDay(date, selectedDate);
                  const isToday = isSameDay(date, new Date());
                  const isBeforeMin = minDate && isBefore(date, startOfDay(minDate));

                  const baseClasses =
                    'flex items-center justify-center rounded-full text-sm w-9 h-9 transition-all select-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue/20';
                  const outsideClasses = inMonth ? 'text-slate-800' : 'text-slate-300';
                  const todayClasses = isToday && !isSelected ? 'font-bold text-blue-600 bg-blue-50' : '';
                  
                  let style = {};
                  let stateClasses = 'hover:bg-slate-100 hover:shadow-sm active:scale-95';

                  if (isBeforeMin) {
                    stateClasses = 'opacity-30 cursor-not-allowed hover:bg-transparent';
                  } else if (isSelected) {
                    stateClasses = 'hover:opacity-90 shadow-sm';
                    style = { backgroundColor: PRIMARY, color: '#ffffff' };
                  }

                  return (
                    <button
                      type="button"
                      key={date.toISOString()}
                      onClick={() => !isBeforeMin && handleDayClick(date)}
                      disabled={isBeforeMin}
                      className={cn(baseClasses, outsideClasses, todayClasses, stateClasses)}
                      style={style}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderMonthGrid = () => {
      const year = visibleDate.getFullYear();
      const months = Array.from({ length: 12 }).map((_, i) => ({
        index: i,
        label: format(new Date(year, i, 1), 'MMM')
      }));
      return (
        <div className="grid grid-cols-3 gap-3 px-3 pb-3 pt-1">
          {months.map(m => (
            <button
              type="button"
              key={m.index}
              onClick={() => handleMonthCellClick(m.index)}
              className="h-9 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 border border-slate-100 hover:border-slate-300 hover:bg-white transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {m.label}
            </button>
          ))}
        </div>
      );
    };

    const renderYearGrid = () => {
      const currentYear = visibleDate.getFullYear();
      const startYear = Math.floor(currentYear / 12) * 12;
      const years = Array.from({ length: 12 }).map((_, i) => startYear + i);
      return (
        <div className="grid grid-cols-3 gap-3 px-3 pb-3 pt-1">
          {years.map(y => {
            const isCurrent = y === currentYear;
            const style = isCurrent
              ? { backgroundColor: PRIMARY, color: '#ffffff', borderColor: PRIMARY }
              : {};
            return (
              <button
                type="button"
                key={y}
                onClick={() => handleYearCellClick(y)}
                className={cn(
                  'h-9 rounded-lg text-sm font-medium border transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue/20',
                  isCurrent
                    ? 'shadow-sm hover:opacity-90'
                    : 'text-slate-700 bg-slate-50 border-slate-100 hover:border-slate-300 hover:bg-white'
                )}
                style={style}
              >
                {y}
              </button>
            );
          })}
        </div>
      );
    };

    const currentYear = visibleDate.getFullYear();
    let headerLabel = '';
    if (viewMode === VIEW_MODES.DAY) {
      headerLabel = format(visibleDate, 'MMMM yyyy');
    } else if (viewMode === VIEW_MODES.MONTH) {
      headerLabel = String(currentYear);
    } else {
      const startYear = Math.floor(currentYear / 12) * 12;
      const endYear = startYear + 11;
      headerLabel = startYear + ' - ' + endYear;
    }

    const renderViewModeSwitch = () => (
      <div className="inline-flex items-center rounded-lg bg-slate-100 p-1 gap-1">
        <button
          type="button"
          onClick={() => setView(VIEW_MODES.DAY)}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-all',
            viewMode === VIEW_MODES.DAY
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          )}
        >
          Day
        </button>
        <button
          type="button"
          onClick={() => setView(VIEW_MODES.MONTH)}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-all',
            viewMode === VIEW_MODES.MONTH
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          )}
        >
          Month
        </button>
        <button
          type="button"
          onClick={() => setView(VIEW_MODES.YEAR)}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-all',
            viewMode === VIEW_MODES.YEAR
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          )}
        >
          Year
        </button>
      </div>
    );

    return (
      <div className={cn("flex flex-col gap-2 w-full", className)}>
        {label && (
          <Label className="text-neutral font-medium text-[15px]">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </Label>
        )}
        
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={cn(
            'flex h-12 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:border-blue focus:ring-2 focus:ring-blue/20 disabled:cursor-not-allowed disabled:opacity-50 transition-all hover:bg-slate-50',
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300',
            !selectedDate && 'text-gray-500'
          )}
        >
          <span className="truncate">
            {selectedDate ? format(selectedDate, 'PPP') : placeholder}
          </span>
          <CalendarIcon className="h-4 w-4 opacity-50 flex-shrink-0" />
        </button>

        {isOpen && createPortal(
          <div
            ref={popoverRef}
            className="w-auto p-0 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden animate-in fade-in-0 zoom-in-95"
            style={{ minWidth: '360px' }}
          >
            <div className="p-4 min-w-[360px]">
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                
                <div className="font-semibold text-slate-800">
                  {headerLabel}
                </div>

                <button
                  type="button"
                  onClick={handleNext}
                  className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>

              <div className="mb-4 flex justify-center">
                {renderViewModeSwitch()}
              </div>

              {viewMode === VIEW_MODES.DAY && renderDayGrid()}
              {viewMode === VIEW_MODES.MONTH && renderMonthGrid()}
              {viewMode === VIEW_MODES.YEAR && renderYearGrid()}
            </div>
          </div>,
          document.body
        )}

        {error && (
          <p className="text-xs text-red-500 font-medium animate-slide-down">
            {error.message || error}
          </p>
        )}
      </div>
    );
  }
);

SingleDatePicker.displayName = 'SingleDatePicker';

export default SingleDatePicker;